import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import { saveBlob } from '@/api/client';
import { Tabs } from '@/components/shell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  Modal,
} from '@/components/ui';
import { Fact, FactList, Prose } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { AdminCompanyRecord } from '@/pages/admin/CompanyDetailPage';
import { AdminStudentRecord } from '@/pages/admin/StudentDetailPage';
import { INTERNSHIP_TONES, VERIFICATION_TONES, humanizeStatus } from '@/features/admin/status';
import { CandidateRestrictionBadge } from '@/features/applications/CandidateRestriction';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { INTERVIEW_LABELS, INTERVIEW_TONES } from '@/features/interviews/status';
import { OFFER_LABELS, OFFER_TONES } from '@/features/offers/status';
import { DocumentViewer } from '@/features/verification/DocumentViewer';
import { historyTitle, isTimelineNote } from '@/features/applications/history';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, formatFileSize, humanizeWorkMode } from '@/utils/format';
import { formatPhone } from '@/utils/phone';

/**
 * The application and the two records it joins. The student and company tabs
 * are the full records, decisions included, so an admin never has to leave
 * the application to act on either side of it.
 */
const SECTIONS = [
  { key: 'application', label: 'Application', icon: 'fileText' },
  { key: 'student', label: 'Student', icon: 'user' },
  { key: 'company', label: 'Company', icon: 'building' },
];

function applicationLabel(status) {
  return APPLICATION_LABELS[status] ?? humanizeStatus(status);
}

/**
 * One application, on its own.
 *
 * Still metadata, not correspondence: the cover letter, the answers and the
 * company's internal note are not sent to the console. What is here is the
 * record around the application -- the CV it went with, the company, every
 * status move and what it led to. Billing belongs to the student and stays on
 * the student's page.
 */
export function AdminApplicationDetailPage() {
  const { id } = useParams();
  const [viewingCv, setViewingCv] = useState(false);
  const [viewingLetter, setViewingLetter] = useState(null);
  // In the URL, so a refresh or the back button lands on the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const section = SECTIONS.some((tab) => tab.key === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'application';
  const showSection = (key) => setSearchParams(key === 'application' ? {} : { tab: key });

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-application', id],
    queryFn: () => adminApi.getApplication(id),
  });

  if (isPending) return <LoadingState label="Loading the application…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const { company, internship, resume } = data;

  return (
    <div>
      <RecordHeader
        backTo={paths.admin.recruitment}
        backLabel="Applications"
        eyebrow="Application"
        title={`${data.student_name} → ${internship.title}`}
        subtitle={`${company.name} · applied ${formatDate(data.submitted_at ?? data.created_at)}`}
        badges={
          <>
            <Badge dot tone={APPLICATION_TONES[data.status] ?? 'neutral'}>
              {applicationLabel(data.status)}
            </Badge>
            {/* The company sees the same marker and cannot act; an admin
                still can. */}
            <CandidateRestrictionBadge row={data} />
          </>
        }
        actions={
          <>
            {resume && (
              <Button variant="secondary" size="sm" onClick={() => setViewingCv(true)}>
                <Icon name="eye" size={16} />
                View CV
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => showSection('company')}>
              <Icon name="building" size={16} />
              View company
            </Button>
            <Button variant="secondary" size="sm" onClick={() => showSection('student')}>
              <Icon name="user" size={16} />
              View student
            </Button>
          </>
        }
        facts={[
          {
            label: 'Match',
            value: data.match_score == null ? '—' : `${Math.round(data.match_score)}%`,
          },
          {
            label: 'In this stage',
            value: `${data.days_in_stage} ${data.days_in_stage === 1 ? 'day' : 'days'}`,
          },
          { label: 'Interviews', value: data.interviews.length },
          { label: 'Offers', value: data.offers.length },
        ]}
      />

      <Tabs
        label="Application section"
        tabs={SECTIONS}
        value={section}
        onChange={showSection}
        className="mb-6"
      />

      {section === 'student' && <AdminStudentRecord studentId={data.student_id} embedded />}
      {section === 'company' && <AdminCompanyRecord companyId={company.id} embedded />}

      {section === 'application' && (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 space-y-6">
            <Card>
              <CardHeader title="Application" />
              <FactList>
                <Fact label="Candidate">
                  <button
                    type="button"
                    onClick={() => showSection('student')}
                    className="text-brand-700 hover:underline"
                  >
                    {data.student_name}
                  </button>
                </Fact>
                <Fact label="Email" value={data.student_email} />
                <Fact label="University" value={data.university} />
                <Fact label="Status" value={applicationLabel(data.status)} />
                <Fact label="Applied" value={formatDate(data.submitted_at ?? data.created_at)} />
                <Fact
                  label="Reviewed"
                  value={data.reviewed_at ? formatDate(data.reviewed_at) : null}
                />
                <Fact
                  label="Decided"
                  value={data.decided_at ? formatDate(data.decided_at) : null}
                />
              </FactList>
              {/* Shown to the student when they were turned down, so not private. */}
              <Prose title="Rejection reason">{data.rejection_reason}</Prose>
            </Card>

            <Card>
              <CardHeader title="Status history" description="Every move, oldest first." />
              {data.history.length === 0 ? (
                <EmptyState compact title="No moves recorded" />
              ) : (
                <ol className="border-line space-y-4 border-l pl-4">
                  {data.history.map((entry, index) => (
                    <li key={`${entry.created_at}-${index}`} className="relative">
                      <span className="bg-brand-500 absolute top-1.5 -left-[21px] size-2.5 rounded-full" />
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={APPLICATION_TONES[entry.to_status] ?? 'neutral'}>
                          {historyTitle(entry, applicationLabel)}
                        </Badge>
                        {entry.from_status && !isTimelineNote(entry) && (
                          <span className="text-ink-muted text-xs">
                            from {applicationLabel(entry.from_status)}
                          </span>
                        )}
                      </div>
                      <p className="text-ink-muted mt-1 text-xs">
                        {formatDate(entry.created_at)} ·{' '}
                        {entry.changed_by_name ?? 'Moved by the platform'}
                      </p>
                      {entry.note && <p className="text-ink mt-1 text-sm">{entry.note}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <Card>
              <CardHeader title="Interviews" />
              {data.interviews.length === 0 ? (
                <EmptyState compact title="No interviews booked" />
              ) : (
                <ol className="space-y-3">
                  {data.interviews.map((row) => (
                    <li key={row.id} className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-ink text-sm font-medium">
                          Round {row.round_number} · {row.title}
                        </p>
                        <p className="text-ink-muted text-xs">
                          {formatDate(row.scheduled_at)} · {humanizeStatus(row.mode)} ·{' '}
                          {row.duration_minutes} min
                          {row.scheduled_by_name ? ` · booked by ${row.scheduled_by_name}` : ''}
                        </p>
                      </div>
                      <Badge tone={INTERVIEW_TONES[row.status] ?? 'neutral'}>
                        {INTERVIEW_LABELS[row.status] ?? humanizeStatus(row.status)}
                      </Badge>
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <Card>
              <CardHeader title="Offers" />
              {data.offers.length === 0 ? (
                <EmptyState compact title="No offer made" />
              ) : (
                <ol className="space-y-3">
                  {data.offers.map((row) => (
                    <li key={row.id} className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-ink text-sm font-medium">{row.title}</p>
                        <p className="text-ink-muted text-xs">
                          Starts {formatDate(row.starts_on)} · respond by{' '}
                          {formatDate(row.respond_by)}
                          {row.sent_at ? ` · sent ${formatDate(row.sent_at)}` : ' · not sent yet'}
                        </p>
                        {row.withdrawal_reason && (
                          <p className="text-ink-muted mt-1 text-xs">
                            Withdrawn: {row.withdrawal_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge tone={OFFER_TONES[row.status] ?? 'neutral'}>
                          {OFFER_LABELS[row.status] ?? humanizeStatus(row.status)}
                        </Badge>
                        {/* A draft's letter has reached nobody, so only a sent
                          offer has one to open. */}
                        {row.sent_at && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setViewingLetter(row)}
                          >
                            <Icon name="fileText" size={15} />
                            View letter
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader title="CV" />
              {resume ? (
                <>
                  <p className="text-ink text-sm break-all">{resume.file_name}</p>
                  <p className="text-ink-muted text-xs">{formatFileSize(resume.size_bytes)}</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => setViewingCv(true)}
                  >
                    <Icon name="eye" size={16} />
                    View CV
                  </Button>
                  {/* The same audited download the document queue uses. */}
                  <p className="text-ink-muted mt-2 text-xs">
                    Opening the CV is recorded in the audit log.
                  </p>
                </>
              ) : (
                <EmptyState
                  compact
                  title="No CV"
                  description="Sent without one, or the student has since deleted it."
                />
              )}
            </Card>

            <Card>
              <CardHeader
                title="Company"
                action={
                  <button
                    type="button"
                    onClick={() => showSection('company')}
                    className="text-brand-700 text-sm font-medium hover:underline"
                  >
                    Open
                  </button>
                }
              />
              <p className="text-ink font-medium">{company.name}</p>
              <Badge
                dot
                tone={VERIFICATION_TONES[company.verification_status] ?? 'neutral'}
                className="mt-1"
              >
                {humanizeStatus(company.verification_status)}
              </Badge>
              <FactList columns={1} className="mt-4">
                <Fact label="Industry" value={company.industry} />
                <Fact label="Location" value={company.location} />
                <Fact label="Size" value={company.company_size} />
                <Fact label="Contact email" value={company.contact_email} />
                <Fact label="Contact phone" value={formatPhone(company.contact_phone)} />
                <Fact label="Website" value={company.website} href={company.website} />
                <Fact label="LinkedIn" value={company.linkedin_url} href={company.linkedin_url} />
              </FactList>
            </Card>

            <Card>
              <CardHeader
                title="Listing"
                action={
                  <Link
                    to={paths.admin.internshipDetail(internship.id)}
                    className="text-brand-700 text-sm font-medium hover:underline"
                  >
                    Open
                  </Link>
                }
              />
              <p className="text-ink font-medium">{internship.title}</p>
              <Badge tone={INTERNSHIP_TONES[internship.status] ?? 'neutral'} className="mt-1">
                {humanizeStatus(internship.status)}
              </Badge>
              <FactList columns={1} className="mt-4">
                <Fact label="Work mode" value={humanizeWorkMode(internship.work_mode)} />
                <Fact label="Location" value={internship.location} />
                <Fact
                  label="Deadline"
                  value={
                    internship.application_deadline
                      ? formatDate(internship.application_deadline)
                      : null
                  }
                />
              </FactList>
            </Card>
          </div>
        </div>
      )}

      <OfferLetterViewer offer={viewingLetter} onClose={() => setViewingLetter(null)} />

      {resume && (
        <DocumentViewer
          open={viewingCv}
          onClose={() => setViewingCv(false)}
          title={`${data.student_name} — CV`}
          fileName={resume.file_name}
          contentType={resume.content_type}
          queryKey={['resume', resume.id]}
          loadUrl={(options) => adminApi.getDocumentDownloadUrl('resume', resume.id, options)}
        />
      )}
    </div>
  );
}

/**
 * The offer letter as the candidate received it. The API returns the PDF itself
 * (not a signed link), so it is framed from a blob URL and saved from the same
 * blob -- one fetch, one audit entry, per opening.
 */
function OfferLetterViewer({ offer, onClose }) {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-offer-letter', offer?.id],
    queryFn: async () => {
      const { blob, filename } = await adminApi.getOfferLetter(offer.id);
      const pdf = new Blob([blob], { type: 'application/pdf' });
      return { pdf, filename, src: URL.createObjectURL(pdf) };
    },
    enabled: Boolean(offer),
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!data?.src) return undefined;
    return () => URL.revokeObjectURL(data.src);
  }, [data?.src]);

  if (!offer) return null;
  const filename = data?.filename?.endsWith('.pdf') ? data.filename : 'offer-letter.pdf';

  return (
    <Modal
      open
      size="xl"
      title={`Offer letter — ${offer.title}`}
      description={offer.company_name}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" disabled={!data} onClick={() => saveBlob(data.pdf, filename)}>
            <Icon name="download" size={15} />
            Download
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {isError ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending ? (
        <LoadingState label="Opening the letter…" />
      ) : (
        <iframe
          src={data.src}
          title={`Offer letter — ${offer.title}`}
          className="border-line bg-surface-muted h-[70vh] w-full rounded-lg border"
        />
      )}
    </Modal>
  );
}
